import { notifications } from '@mantine/notifications';
import { DiscountType, OrderQueryResponseResponseMessage, PaymentMethod } from '@models/ropolo';
// eslint-disable-next-line import/no-cycle
import { orderingSlice } from '@reducers/ordering';
import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { CreateOfflineCustomerPayload } from '../../models/payload/customer';
import { CreatePostPaidOrderPayload, EditPostPaidOrderPayload, RefundOrderPayload } from '../../models/payload/order';
import { CreateCustomerOfflineResponse } from '../../models/response/customer';
import { IError, IErrorResponse } from '../../models/response/error';
import { OrderByIdResponse, OrderDetailsResponse, OrderUpdatedEventResponse } from '../../models/response/order';
import { createOfflineCustomer } from '../../services/customer';
import { createOfflineOrder, getOrder, refundOrderItems, updatePostPay } from '../../services/order';
import messages from '../../shared/constants/messages';
import { createAppAsyncThunk } from '../../utils/reduxUtil';
import {
  AddFactorDetailsAction,
  AddFactorErrorAction,
  AddItemByInput,
  AddMenuItemAction,
  OfflineOrderingState,
} from './types';
import addFactorDetails from './utilities/addFactorDetails';
import addItemByInput from './utilities/addItemByInput';
import addMenuItemByTempId from './utilities/addMenuItemByTempId';
import addItemToMenu from './utilities/addToMenuItem';
import removeDiscountFactorDetails from './utilities/removeDiscountFactorDetails';
import removeItemByTempId from './utilities/removeItemByTempId';
import removeItemFromMenu from './utilities/removeItemFromMenu';
import removeRowItemByTempId from './utilities/removeRowItemByTempId';
import returnOrderItem from './utilities/returnOrderItem';
import selectTopping from './utilities/selectTopping';

const initialState: OfflineOrderingState = {
  customer: undefined,
  deliveryType: undefined,
  menus: [],
  showDetails: false,
  selectedMenuItem: undefined,
  selectedTopping: [],
  selectedAddressId: '',
  modifyModal: false,
  showAddCustomer: false,
  loading: false,
  addCustomerError: undefined,
  paymentType: PaymentMethod.CARD,
  description: undefined,
  tableNumber: undefined,
  preparationTime: 20,
  numberOfCustomer: 0,
  deliveryCost: 0,
  submittedOfflineOrder: undefined,
  newCustomerPhoneNumber: undefined,
  defaultDescriptions: [],
  isEdited: false,
  order: undefined,
  removedItems: [],
  defaultAddress: undefined,
  discountAmount: 0,
  finalDiscountAmount: 0,
  discountType: DiscountType.PERCENTAGE,
  tip: 0,
  canAdd: true,
  couponCode: undefined,
  finalPrice: 0,
  sumOfPackagingCostItems: 0,
  tax: 0,
  sumOfItemsPrice: 0,
  totalPackagingCost: 0,
  sumOfItemToppingsPrice: 0,
  serviceChargeFinalPrice: 0,
  discountPackagingCost: 0,
  serviceChargeCostNumber: 0,
  hasError: false,
  menuItemsCount: 0,
};

export const getOrderById = createAppAsyncThunk<OrderByIdResponse, { id: string; networkStatus: boolean }>(
  'order/getOrderById',
  async (input) => {
    const response = await getOrder({ id: input.id, networkStatus: input?.networkStatus });
    return response;
  }
);

export const submitOfflineOrdering = createAppAsyncThunk<
  OrderQueryResponseResponseMessage,
  CreatePostPaidOrderPayload & { networkStatus: boolean }
>('address/submitOfflineOrder', async (command, { rejectWithValue, dispatch }) => {
  try {
    const response = (await createOfflineOrder({
      payload: command,
    })) as OrderQueryResponseResponseMessage;
    dispatch(getOrderById({ id: response?.data?.id as string, networkStatus: command?.networkStatus }));

    return response;
  } catch (err) {
    notifications.show({
      message: messages.ORDER_SUBMIT_REJECTED,
      color: 'red',
    });

    return rejectWithValue((err as AxiosError)?.response?.data);
  }
});

export const updatePostPayHandler = createAppAsyncThunk<
  OrderUpdatedEventResponse,
  EditPostPaidOrderPayload & { networkStatus: boolean }
>('order/updatePostPay', async (input, { rejectWithValue, dispatch }) => {
  try {
    const response = await updatePostPay({ command: input, networkStatus: input?.networkStatus });
    dispatch(orderingSlice.actions.removeDiscount());
    // dispatch(getOrderById({ id: input?.orderId as string, networkStatus: input.networkStatus }));
    notifications.show({
      message: messages.OPERATION_SUBMIT_SUCCESS,
      color: 'green',
    });
    return response;
  } catch (err) {
    notifications.show({
      message: ((err as AxiosError)?.response?.data as IErrorResponse).data?.message,
      color: 'red',
    });

    return rejectWithValue((err as AxiosError)?.response?.data);
  }
});

export const refundOrderItem = createAppAsyncThunk<
  OrderUpdatedEventResponse,
  RefundOrderPayload & { networkStatus: boolean }
>('order/refundOrderItem', async (input, { rejectWithValue, dispatch }) => {
  try {
    const response = await refundOrderItems({ command: input });
    if (input.submitType === 'submitPrint') {
      dispatch(getOrderById({ id: input?.orderId as string, networkStatus: input?.networkStatus }));
    }
    notifications.show({
      message: messages.OPERATION_SUBMIT_SUCCESS,
      color: 'green',
    });
    return response;
  } catch (err) {
    return rejectWithValue((err as AxiosError)?.response?.data);
  }
});

export const createCustomerOffline = createAppAsyncThunk<
  CreateCustomerOfflineResponse,
  CreateOfflineCustomerPayload & { networkStatus: boolean }
>('address/create', async (command, { rejectWithValue }) => {
  try {
    const response = await createOfflineCustomer({ payload: command, networkStatus: command?.networkStatus });
    notifications.show({
      message: messages.OPERATION_SUBMIT_SUCCESS,
      color: 'green',
    });
    return response;
  } catch (err) {
    return rejectWithValue((err as AxiosError)?.response?.data);
  }
});

const offlineOrderingSlice = createSlice({
  name: 'offlineOrdering',
  initialState,
  reducers: {
    removeOrder: (state) => ({
      ...state,
      loading: false,
      customer: undefined,
      deliveryType: undefined,
      menus: [],
      showDetails: false,
      selectedMenuItem: undefined,
      selectedTopping: [],
      selectedAddressId: '',
      modifyModal: false,
      showAddCustomer: false,
      addCustomerError: undefined,
      paymentType: PaymentMethod.CARD,
      description: undefined,
      tableNumber: undefined,
      preparationTime: 20,
      submittedOfflineOrder: undefined,
      defaultDescriptions: [],
      canAdd: true,
      defaultAddress: undefined,
      deliveryCost: 0,
      discountAmount: 0,
      discountType: DiscountType.PERCENTAGE,
      isEdited: false,
      newCustomerPhoneNumber: undefined,
      numberOfCustomer: 0,
      order: undefined,
      removedItems: [],
      discountCode: undefined,
      tip: 0,
    }),

    addCustomer: (state, action) => ({
      ...state,
      customer: action.payload,
      selectedAddressId: action?.payload?.addressId,
    }),
    removeCustomer: (state) => ({ ...state, customer: undefined, selectedAddressId: '' }),
    setDeliveryType: (state, action) => ({ ...state, deliveryType: action.payload }),
    setPhoneNumberCreateNewCustomer: (state, action) => ({ ...state, newCustomerPhoneNumber: action?.payload }),
    removeMenu: (state, action) => removeItemFromMenu(state, action.payload),
    toggleDetails: (state, action) => ({ ...state, showDetails: action?.payload }),
    selectMenuItem: (state, action) => ({ ...state, selectedMenuItem: action?.payload, showDetails: true }),
    selectedTopping: (state, action) => selectTopping(state, action.payload),

    addMenu: (state, action: AddMenuItemAction) => addItemToMenu(state, action.payload),
    addItemByTempId: (state, action) => addMenuItemByTempId(state, action.payload),
    addItemByInput: (state, action: AddItemByInput) =>
      addItemByInput(state, action.payload?.tempId, action?.payload?.value),

    removeItemByTempId: (state, action) => removeItemByTempId(state, action.payload),

    removeRowItemByTempId: (state, action) => removeRowItemByTempId(state, action.payload),

    toggleModifyModal: (state, action) => ({ ...state, modifyModal: action.payload }),

    setDeliveryCost: (state, action) => ({ ...state, deliveryCost: action.payload }),

    setDefaultDescription: (state, action) => ({ ...state, defaultDescriptions: action.payload }),

    addSelectedAddress: (state, action) => ({ ...state, selectedAddressId: action?.payload }),

    toggleAddCustomerModal: (state, action) => ({ ...state, showAddCustomer: action?.payload }),

    setPaymentType: (state, action) => ({ ...state, paymentType: action.payload }),

    setDescription: (state, action) => ({ ...state, description: action.payload }),

    emptyBasket: (state) => ({ ...state, menus: [] }),

    setTableNumber: (state, action) => ({ ...state, tableNumber: action.payload }),

    setPreparationTime: (state, action) => ({ ...state, preparationTime: action.payload }),

    addFactorDetails: (state, action: AddFactorDetailsAction) => addFactorDetails(state, action.payload),

    addFactorError: (state, action: AddFactorErrorAction) => ({ ...state, hasError: action?.payload }),

    setEdited: (state, action) => ({ ...state, isEdited: action?.payload }),

    setUpdatedDetails: (state, action) => ({ ...state, ...action.payload }),

    removeDiscountFactorDetails: (state) => removeDiscountFactorDetails(state),

    returnOrderItem: (state, action) => returnOrderItem(state, action?.payload),
  },
  extraReducers: (builder: ActionReducerMapBuilder<OfflineOrderingState>) => {
    builder.addCase(createCustomerOffline.pending, (state) => ({
      ...state,
      loading: true,
      addCustomerError: undefined,
    }));
    builder.addCase(createCustomerOffline.fulfilled, (state, action) => ({
      ...state,
      customer: {
        ...state.customer,
        address: action.payload.data?.detail,
        firstName: action.payload.data?.firstName,
        id: action?.payload?.data?.id,
        lastName: action?.payload?.data?.lastName,
        phoneNumber: action?.payload?.data?.phoneNumber,
      },
      loading: false,
      selectedAddressId: action?.payload?.data?.addressId || '',
      showAddCustomer: false,
    }));
    builder.addCase(createCustomerOffline.rejected, (state, action) => ({
      ...state,
      loading: false,
      addCustomerError: (action?.payload as IErrorResponse).data!.errors as IError,
    }));

    builder.addCase(submitOfflineOrdering.pending, (state) => ({
      ...state,
      loading: true,
      submittedOfflineOrder: undefined,
    }));
    builder.addCase(submitOfflineOrdering.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));
    builder.addCase(submitOfflineOrdering.rejected, (state, action) => ({
      ...state,
      loading: false,
      addCustomerError: (action?.payload as IErrorResponse)?.data?.errors as IError,
    }));

    builder.addCase(getOrderById.pending, (state) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(getOrderById.fulfilled, (state, action) => ({
      ...state,
      submittedOfflineOrder: action.payload.data,
    }));
    builder.addCase(getOrderById.rejected, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(updatePostPayHandler.pending, (state) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(updatePostPayHandler.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      submittedOfflineOrder: action.payload.data as OrderDetailsResponse,
    }));
    builder.addCase(updatePostPayHandler.rejected, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(refundOrderItem.pending, (state) => ({ ...state, loading: true }));
    builder.addCase(refundOrderItem.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));
    builder.addCase(refundOrderItem.rejected, (state) => ({ ...state, loading: false }));
  },
});

export default offlineOrderingSlice;
